'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { VisuallyHidden } from '../visually-hidden';
import { TableHeaderCellContext } from './context/table-header-cell.context';
import { TableRootContext } from './context/table-root.context';
import { tableSortButtonRecipe } from './styles/table-sort-button.styles';
import { TableSortIcon } from './table-sort-icon';
import type { TableSortButtonPropsInterface } from './types/table-sort-button.types';

/**
 * @since 10.0.0
 */
export const TableSortButton = forwardRef<HTMLSpanElement, TableSortButtonPropsInterface>(
  (props: TableSortButtonPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;

    const { sortDirection } = useContext(TableRootContext);
    const { align, sorted } = useContext(TableHeaderCellContext);

    const styles = css(tableSortButtonRecipe.raw({ align }), cssProp);

    return (
      <span
        ref={forwardedRef}
        role="button"
        tabIndex={0}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Table.SortButton"
        className={cx('c11n', 'group', styles, className)}
        {...rest}
      >
        {children}
        <TableSortIcon />
        <VisuallyHidden>
          {sorted && sortDirection !== 'none' ? `, sorted ${sortDirection}` : ', sortable column'}
        </VisuallyHidden>
      </span>
    );
  },
);

TableSortButton.displayName = 'Table.SortButton';
